import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { StatusPill } from "@/components/data-table/StatusPill";
import { formatCurrency, formatDateForDisplay } from "@/lib/formatting";
import type { Revenue } from "@shared/schema";
import type { ColorMapping } from "@/components/data-table/types";

interface UnassignedRevenuesTableProps {
  revenues: Revenue[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const paymentStatusColors: Record<string, ColorMapping> = {
  "Tak": { bg: "bg-green-100", text: "text-green-800" },
  "Nie": { bg: "bg-red-100", text: "text-red-800" },
};

export function UnassignedRevenuesTable({
  revenues,
  currentPage,
  totalPages,
  onPageChange,
}: UnassignedRevenuesTableProps) {
  // Calculate totals for footer
  const totals = revenues.reduce(
    (acc, revenue) => {
      const netto = parseFloat(revenue.kwotaNetto || "0") || 0;
      const brutto = parseFloat(revenue.kwotaBrutto || "0") || 0;
      return {
        netto: acc.netto + netto,
        brutto: acc.brutto + brutto,
      };
    },
    { netto: 0, brutto: 0 }
  );

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Nr</TableHead>
            <TableHead className="w-[120px]">Nr FVAT</TableHead>
            <TableHead className="w-[200px]">Nazwa klienta</TableHead>
            <TableHead className="w-[130px] text-right">Kwota netto</TableHead>
            <TableHead className="w-[130px] text-right">Kwota brutto</TableHead>
            <TableHead className="w-[120px]">Data wystawienia</TableHead>
            <TableHead className="w-[100px] text-center">Zapłacono</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {revenues.map((revenue) => (
            <TableRow key={revenue.id}>
              <TableCell className="font-mono">{revenue.nrPorzadkowy}</TableCell>
              <TableCell className="font-mono">{revenue.nrFvat || "-"}</TableCell>
              <TableCell>{revenue.nazwaKlienta || "-"}</TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(revenue.kwotaNetto)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(revenue.kwotaBrutto)}
              </TableCell>
              <TableCell>{formatDateForDisplay(revenue.dataWystawienia)}</TableCell>
              <TableCell className="text-center">
                <StatusPill
                  value={revenue.czyZaplacono || "Nie"}
                  colorMapping={paymentStatusColors}
                  testIdType="paymentStatus"
                />
              </TableCell>
            </TableRow>
          ))}
          {revenues.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={7}
                className="text-center text-muted-foreground py-8"
              >
                Brak nieprzypisanych faktur
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {revenues.length > 0 && (
          <TableFooter>
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell colSpan={3} className="text-right">
                Suma ({revenues.length} {revenues.length === 1 ? "faktura" : revenues.length < 5 ? "faktury" : "faktur"})
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totals.netto)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totals.brutto)}
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableFooter>
        )}
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          <p className="text-sm text-muted-foreground">
            Strona {currentPage} z {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Poprzednia
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Następna
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
